import './SeriesFilter.scss'

import AdminContext from 'lib/context/AdminContext'
import Series from 'pages/Home/Series'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { SizeMeProps, withSize } from 'react-sizeme'

import AddSeriesDialog from './admin/AddSeriesDialog'
import DeleteSeriesDialog from './admin/DeleteSeriesDialog'
import SeriesFilterAdminControls from './admin/SeriesFilterAdminControls'
import MoreSeriesDropdown from './MoreSeriesDropdown'
import SeriesDropdown from './SeriesDropdown'
import SeriesFilterItem from './SeriesFilterItem'

export interface SeriesFilterProps extends RouteComponentProps, SizeMeProps {
  clearQueryKeys?: string[]
  selectedSeries?: Series
  series: Series[]
}

function estimateSeriesFilterItemElementWidth(title: string): number {
  // 6.4 = estimated factor based on average length of characters, assuming
  //   bold Lato 13px with letter-spacing: 0.3px

  return title.length * 6.4 + 20
}

export default withSize()(
  withRouter(function SeriesFilter(props: SeriesFilterProps) {
    const { clearQueryKeys, selectedSeries, series: seriesList, size } = props
    const { showAdminControls } = useContext(AdminContext)
    const [t] = useTranslation()
    const [dialogState, setDialogState] = useState({ open: null, series: undefined })

    const closeDialog = () => setDialogState({ open: null, series: undefined })

    const estimatedElementWidths = seriesList.map(series => estimateSeriesFilterItemElementWidth(series.title))
    const estimatedAllWidth = estimateSeriesFilterItemElementWidth(t('home.series.all.name'))
    const estimatedMoreWidth = estimateSeriesFilterItemElementWidth(t('home.series.more'))

    const inHeaderView = seriesList.slice()
    const inDropdownView = [] as Series[]

    let estTotalContentWidth = estimatedElementWidths.reduce((accum, curr) => accum + curr, 0) + estimatedAllWidth
    while (estTotalContentWidth > size.width && estimatedElementWidths.length > 0) {
      const series = inHeaderView.pop()
      const estimatedElementWidth = estimatedElementWidths.pop()
      inDropdownView.push(series)
      estTotalContentWidth -= estimatedElementWidth
      if (inDropdownView.length === 1) {
        estTotalContentWidth += estimatedMoreWidth
      }
    }
    inDropdownView.reverse()

    const shouldRenderDropdown = inDropdownView.length > 0
    const shouldRenderDropdownOnly = shouldRenderDropdown && inDropdownView.length >= seriesList.length - 1

    function renderDropdown() {
      if (shouldRenderDropdownOnly) {
        return <SeriesDropdown series={seriesList} selectedSeries={selectedSeries} />
      }

      return (
        <MoreSeriesDropdown
          openDeleteSeriesDialog={(series: Series) => setDialogState({ open: 'delete', series })}
          openEditSeriesDialog={(series: Series) => setDialogState({ open: 'edit', series })}
          series={inDropdownView}
          selectedSeries={selectedSeries}
        />
      )
    }

    return (
      <div className="series-filter">
        {!shouldRenderDropdownOnly && (
          <ul className="series-filter__list">
            <SeriesFilterItem selectedSeries={selectedSeries} />
            {inHeaderView.map(series => (
              <SeriesFilterItem
                clearQueryKeys={clearQueryKeys}
                key={series.id}
                openDeleteSeriesDialog={(series: Series) => setDialogState({ open: 'delete', series })}
                openEditSeriesDialog={(series: Series) => setDialogState({ open: 'edit', series })}
                selectedSeries={selectedSeries}
                series={series}
              />
            ))}
          </ul>
        )}
        {shouldRenderDropdown && renderDropdown()}
        {showAdminControls && (
          <SeriesFilterAdminControls openAddSeriesDialog={() => setDialogState({ open: 'add', series: undefined })} />
        )}
        {(dialogState.open === 'edit' || dialogState.open === 'add') && (
          <AddSeriesDialog onRequestClose={closeDialog} initialSeries={dialogState.series} />
        )}
        {dialogState.open === 'delete' && (
          <DeleteSeriesDialog onRequestClose={closeDialog} series={dialogState.series} />
        )}
      </div>
    )
  })
)
